import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FaClipboard } from 'react-icons/fa';
import { assetUrl, copyImageToClipboard } from '../../utils/photoslopApi';

function useCopyImage() {
  const [busyPath, setBusyPath] = useState(null);
  const [copiedPath, setCopiedPath] = useState(null);

  const copy = useCallback(async (imagePath) => {
    if (busyPath) return;
    setBusyPath(imagePath);
    try {
      await copyImageToClipboard(imagePath);
      setCopiedPath(imagePath);
      toast.success('Copied — paste anywhere');
      window.setTimeout(() => setCopiedPath(null), 2500);
    } catch (err) {
      toast.error(err?.message || 'Could not copy image');
    } finally {
      setBusyPath(null);
    }
  }, [busyPath]);

  return { copy, busyPath, copiedPath };
}

function ActionBar({
  imagePath,
  onUseAsRoot,
  onAddToDeck,
  addingDeck,
  copy,
  busyPath,
  copiedPath,
  size = 'lg',
}) {
  const busy = addingDeck === imagePath;
  const copying = busyPath === imagePath;
  const copied = copiedPath === imagePath;
  const text = size === 'lg' ? 'text-[18px]' : 'text-[14px]';
  const pad = size === 'lg' ? 'min-h-[44px] px-4 py-2.5' : 'min-h-[40px] px-3 py-2';

  return (
    <div className={`flex flex-wrap gap-2 ${size === 'lg' ? 'w-full max-w-md' : ''}`}>
      <button
        type="button"
        disabled={copying}
        onClick={() => copy(imagePath)}
        className={`btn btn-green flex flex-1 items-center justify-center gap-2 rounded-xl font-['VT323'] ${text} ${pad} disabled:opacity-60`}
      >
        <FaClipboard className={size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
        {copying ? 'Copying…' : copied ? 'Copied!' : 'Copy image'}
      </button>
      <button
        type="button"
        onClick={() => onUseAsRoot(imagePath)}
        className={`rounded-xl border border-black/10 bg-white font-['VT323'] ${text} ${pad} text-black/60 hover:border-[#00FF9F]/40 hover:text-black/80`}
      >
        Set @1
      </button>
      {onAddToDeck ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAddToDeck(imagePath)}
          className={`rounded-xl border border-black/10 bg-white font-['VT323'] ${text} ${pad} text-black/60 hover:text-black/80 disabled:opacity-50`}
        >
          {busy ? '…' : 'Deck'}
        </button>
      ) : null}
    </div>
  );
}

function ThumbSlot({ slot, active, onSelect }) {
  const { status, imagePath } = slot;

  if (status === 'running' || status === 'queued') {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={`relative aspect-square w-16 shrink-0 overflow-hidden rounded-lg border-2 ${
          active ? 'border-[#00FF9F]' : 'border-dashed border-black/15'
        } bg-white/80`}
      >
        <div className="flex h-full items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#00FF9F] border-t-transparent" />
        </div>
      </button>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex aspect-square w-16 shrink-0 items-center justify-center rounded-lg border-2 border-[#FF1493]/30 bg-[#FF1493]/5">
        <span className="font-['VT323'] text-[10px] text-[#FF1493]">Fail</span>
      </div>
    );
  }

  if (status === 'done' && imagePath) {
    const src = assetUrl(imagePath);
    return (
      <button
        type="button"
        onClick={onSelect}
        className={`relative aspect-square w-16 shrink-0 overflow-hidden rounded-lg border-2 ${
          active ? 'border-[#00FF9F] ring-2 ring-[#00FF9F]/25' : 'border-black/10'
        }`}
      >
        <img src={src} alt="" className="h-full w-full object-cover" />
      </button>
    );
  }

  return null;
}

export default function PhotoslopOutput({
  slots,
  history,
  onUseAsRoot,
  onAddToDeck,
  addingDeck,
  generating,
}) {
  const { copy, busyPath, copiedPath } = useCopyImage();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);

  useEffect(() => {
    const lastDone = slots.map((s, i) => ({ s, i })).filter((x) => x.s.status === 'done').pop();
    if (lastDone) setFocusIndex(lastDone.i);
  }, [slots]);

  const doneSlots = useMemo(
    () => slots.map((s, i) => ({ ...s, index: i })).filter((s) => s.status === 'done' && s.imagePath),
    [slots],
  );

  const focusSlot = slots[focusIndex] ?? slots[0];
  const hasSlots = slots.length > 0;
  const heroPath =
    focusSlot?.status === 'done' && focusSlot.imagePath
      ? focusSlot.imagePath
      : doneSlots[doneSlots.length - 1]?.imagePath;

  const isLoading =
    generating ||
    slots.some((s) => s.status === 'running' || s.status === 'queued') ||
    (hasSlots && !heroPath && !slots.some((s) => s.status === 'failed'));

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#F4F3F0]">
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-3">
        {heroPath ? (
          <div className="flex w-full max-w-2xl flex-col items-center gap-4">
            <div className="relative w-full overflow-hidden rounded-2xl border-2 border-[#00FF9F]/35 bg-white shadow-lg">
              <img
                src={assetUrl(heroPath)}
                alt=""
                className="mx-auto max-h-[min(52vh,520px)] w-full object-contain"
              />
            </div>
            <ActionBar
              imagePath={heroPath}
              onUseAsRoot={onUseAsRoot}
              onAddToDeck={onAddToDeck}
              addingDeck={addingDeck}
              copy={copy}
              busyPath={busyPath}
              copiedPath={copiedPath}
              size="lg"
            />
            {slots.length > 1 ? (
              <div className="flex w-full max-w-2xl gap-2 overflow-x-auto pb-1">
                {slots.map((slot, i) => (
                  <ThumbSlot
                    key={i}
                    slot={slot}
                    active={focusIndex === i}
                    onSelect={() => setFocusIndex(i)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-[#00FF9F] border-t-transparent" />
            <p className="font-['VT323'] text-[20px] text-black/45">Generating…</p>
            <p className="max-w-xs font-['VT323'] text-[14px] leading-snug text-black/30">
              Your image will show here. Copy will be one tap when it&apos;s ready.
            </p>
          </div>
        ) : slots.some((s) => s.status === 'failed') ? (
          <div className="text-center">
            <p className="font-['VT323'] text-[20px] text-[#FF1493]">Generation failed</p>
            <p className="mt-2 font-['VT323'] text-[14px] text-black/40">
              {slots.find((s) => s.error)?.error || 'Try again from the dock below'}
            </p>
          </div>
        ) : (
          <div className="max-w-sm text-center">
            <p className="font-['VT323'] text-[22px] text-black/30">Your image appears here</p>
            <p className="mt-2 font-['VT323'] text-[15px] leading-snug text-black/25">
              Pick refs, write a prompt, hit Create — then copy and paste wherever you need it.
            </p>
          </div>
        )}
      </div>

      {history.length > 0 ? (
        <div className="flex-shrink-0 border-t border-black/5 px-4 py-2">
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="font-['VT323'] text-[15px] text-black/40 hover:text-black/60"
          >
            {historyOpen ? '▾' : '▸'} Past images ({history.length})
          </button>
          <AnimatePresence>
            {historyOpen ? (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
                  {history.map((item) => (
                    <div
                      key={item.path}
                      className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-black/8 bg-white"
                    >
                      <img
                        src={assetUrl(item.thumbnail || item.path)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 flex flex-col justify-end gap-0.5 bg-gradient-to-t from-black/75 to-transparent p-1 opacity-0 transition group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => copy(item.path)}
                          className="rounded bg-[#00FF9F] px-1 py-0.5 font-['VT323'] text-[11px] text-black"
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => onUseAsRoot(item.path)}
                          className="rounded bg-white/90 px-1 py-0.5 font-['VT323'] text-[10px] text-black"
                        >
                          @1
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      ) : null}
    </div>
  );
}
